import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, Euro, Eye, Landmark, Loader2, User, Wallet } from 'lucide-react';
import ReportingFilter from './ReportingFilter';
import ViewMoreModal from '../modals/ViewMoreModal';
import { ReportingData } from '../../types/index';
import { fetchReportingData } from '../../services/api/sepaService';

const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const monthLabel = (mes: number): string => MONTH_NAMES[mes - 1] ?? `Mes ${mes}`;

/** Formatea importes al uso español: 1.035,00 */
const formatNumber = (num: number): string => {
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
};

interface StatCardProps {
    icon: React.ReactNode;
    label: string;
    value: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value }) => (
    <div className="flex items-center justify-center md:justify-start p-6 rounded-lg bg-white shadow-sm">
        {icon}
        <div>
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-xl font-bold">{value}</div>
        </div>
    </div>
);

const Reporting: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [reportingData, setReportingData] = useState<ReportingData[]>([]);
    const [yearFilter, setYearFilter] = useState('');
    const [monthFilter, setMonthFilter] = useState('');
    const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const data = await fetchReportingData();
                if (cancelled) return;
                setReportingData(data);
                setHasError(false);
            } catch (error) {
                if (cancelled) return;
                console.error('Error cargando los datos de reporte:', error);
                setHasError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, []);

    /** Años realmente presentes en los datos, del más reciente al más antiguo. */
    const years = useMemo(
        () => [...new Set(reportingData.map((item) => item.anio))].sort((a, b) => b - a),
        [reportingData],
    );

    /** Mes más reciente primero: lo que se acaba de cerrar es lo que se consulta. */
    const visibleData = useMemo(() => {
        return reportingData
            .filter((item) => {
                const matchesYear = yearFilter ? item.anio.toString() === yearFilter : true;
                const matchesMonth = monthFilter ? item.mes.toString() === monthFilter : true;
                return matchesYear && matchesMonth;
            })
            .sort((a, b) => b.anio - a.anio || b.mes - a.mes);
    }, [reportingData, yearFilter, monthFilter]);

    /** Totales de lo que se está viendo, no de todo el histórico. */
    const totals = useMemo(() => {
        return visibleData.reduce(
            (acc, item) => {
                const sepaAmount = item.sepaInfo[0]?.montoTotal ?? 0;
                acc.sepa += sepaAmount;
                acc.otherPayments += item.totalPagos;
                acc.total += item.totalPagos + sepaAmount;
                return acc;
            },
            { sepa: 0, otherPayments: 0, total: 0 },
        );
    }, [visibleData]);

    const handleFilter = (yearSelected: string, monthSelected: string) => {
        setYearFilter(yearSelected);
        setMonthFilter(monthSelected);
    };

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Panel de totales — refleja los meses filtrados, no el histórico completo */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6 border-b bg-blue-50">
                <StatCard
                    icon={<CalendarDays className="h-8 w-8 text-blue-500 mr-3" />}
                    label="Meses"
                    value={visibleData.length.toString()}
                />
                <StatCard
                    icon={<Euro className="h-8 w-8 text-green-500 mr-3" />}
                    label="Importe Total"
                    value={`€${formatNumber(totals.total)}`}
                />
                <StatCard
                    icon={<Landmark className="h-8 w-8 text-indigo-500 mr-3" />}
                    label="Domiciliaciones SEPA"
                    value={`€${formatNumber(totals.sepa)}`}
                />
                <StatCard
                    icon={<Wallet className="h-8 w-8 text-purple-500 mr-3" />}
                    label="Otros Pagos"
                    value={`€${formatNumber(totals.otherPayments)}`}
                />
            </div>

            {/* Filtros */}
            <div className="p-6 border-b">
                <ReportingFilter years={years} onFilter={handleFilter} />
            </div>

            {/* Listado por mes */}
            <div className="p-6">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Cargando datos de reporte…
                    </div>
                ) : hasError ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-red-600">
                        <AlertCircle className="h-5 w-5" />
                        No se pudieron cargar los datos de reporte.
                    </div>
                ) : visibleData.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        No se encontraron registros para estos filtros.
                    </div>
                ) : (
                    <div className="space-y-6">
                        {visibleData.map((item) => {
                            const sepa = item.sepaInfo[0] ?? null;
                            const isSepaClosed = sepa !== null && sepa.estado !== 'Abierto';
                            const monthTotal = item.totalPagos + (sepa?.montoTotal ?? 0);

                            return (
                                <div
                                    key={`${item.anio}-${item.mes}`}
                                    className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm"
                                >
                                    {/* Cabecera del mes */}
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
                                        <div className="flex items-center gap-2">
                                            <CalendarDays className="h-5 w-5 text-blue-500" />
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {monthLabel(item.mes)} {item.anio}
                                            </h3>
                                            {sepa && (
                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                        isSepaClosed
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-amber-100 text-amber-800'
                                                    }`}
                                                >
                                                    {isSepaClosed ? 'SEPA Cerrado' : 'SEPA Abierto'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-gray-500">Monto Total Mes</div>
                                            <div className="text-xl font-bold text-gray-900">
                                                €{formatNumber(monthTotal)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Desglose */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {sepa && (
                                            <div className="bg-gray-50 rounded-lg p-6">
                                                <div className="flex items-start justify-between gap-2 mb-6">
                                                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                                        <Landmark className="h-4 w-4 text-indigo-500" />
                                                        Domiciliaciones SEPA
                                                    </h4>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedTransactionId(sepa._id)}
                                                        disabled={!isSepaClosed}
                                                        title={
                                                            isSepaClosed
                                                                ? 'Ver clientes de la remesa'
                                                                : 'La remesa aún no está cerrada'
                                                        }
                                                        aria-label="Ver clientes de la remesa"
                                                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1 transition disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <Euro className="h-4 w-4 text-gray-400" />
                                                        <span className="font-medium">€{formatNumber(sepa.montoTotal)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <CalendarDays className="h-4 w-4 text-gray-400" />
                                                        <span>
                                                            {sepa.fechaCierre
                                                                ? new Date(sepa.fechaCierre).toLocaleDateString()
                                                                : 'Sin cerrar'}
                                                        </span>
                                                    </div>
                                                    {sepa.createdBy && (
                                                        <div className="flex items-center gap-2 text-gray-600">
                                                            <User className="h-4 w-4 text-gray-400" />
                                                            <span>{sepa.createdBy}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="bg-gray-50 rounded-lg p-6">
                                            <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-6">
                                                <Wallet className="h-4 w-4 text-purple-500" />
                                                Otros Pagos
                                            </h4>
                                            <div className="flex items-center gap-2 text-sm">
                                                <Euro className="h-4 w-4 text-gray-400" />
                                                <span className="font-medium">€{formatNumber(item.totalPagos)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedTransactionId && (
                <ViewMoreModal
                    isOpen={true}
                    transactionId={selectedTransactionId}
                    onClose={() => setSelectedTransactionId(null)}
                />
            )}
        </div>
    );
};

export default Reporting;
